import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Simplebar from 'simplebar-react';
import { useEventTracking } from 'state/eventTracking';
import { TrackReportExportConfig } from 'state/eventTracking/types';

import * as S from './styles';
import { BaseTableData, DailyStatsTableConfig } from './types';
import useDailyStatsTable from './useDailyStatsTable';

const DEFAULT_CARD_TITLE = 'Daily Stats';

export interface DailyStatsTableCardProps<TData extends BaseTableData>
  extends DailyStatsTableConfig<TData> {
  className?: string;
  reportType?: TrackReportExportConfig['report'];
  subtitle?: string;
  showFooter?: boolean;
  showHeader?: boolean;
  tableLayout?: 'fixed' | 'auto';
  title?: string;
}

const DailyStatsTableCard = <TData extends BaseTableData>(
  props: DailyStatsTableCardProps<TData>,
): React.ReactElement => {
  const {
    columns,
    dailyStats,
    defaultSort,
    exportArgs,
    showFooter = true,
    showHeader = true,
    tableLayout,
    title = DEFAULT_CARD_TITLE,
    subtitle,
    virtual,
    footer,
    reportType,
    className,
  } = props;

  const { trackReportExport } = useEventTracking();

  const { loading, tableInstance } = useDailyStatsTable({
    columns,
    dailyStats,
    defaultSort,
    exportArgs,
    virtual,
    footer,
  });

  const { exportData } = tableInstance;

  const handleExportButtonClick = () => {
    if (!reportType) {
      return;
    }

    exportData('csv', true);
    trackReportExport({ report: reportType });
  };

  return (
    <Card className={className}>
      {showHeader && (
        <CardHeader
          title={title}
          subheader={subtitle}
          action={
            reportType && (
              <Button
                onClick={handleExportButtonClick}
                variant="outlined"
                startIcon={<FileDownloadIcon />}
              >
                Export
              </Button>
            )
          }
        />
      )}

      <CardContent component={Simplebar}>
        <S.DailyStatsTable
          tableLayout={tableLayout}
          isLoading={loading}
          skeletonRows={3}
          table={tableInstance}
          virtual
          footer={showFooter}
        />
      </CardContent>
    </Card>
  );
};

export default DailyStatsTableCard;
